import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { NAV_LINKS } from "../data/content";
import { scrollToTarget } from "../lib/scroll";

export const Logo = ({ onClick }) => (
  <Link to="/" onClick={onClick} data-testid="rnb-logo-link" className="flex items-center group">
    <img
      src="/logo.png"
      alt="Real Natural Bodybuilding by Dani Postigo"
      className="h-12 sm:h-14 w-auto transition-opacity duration-200 group-hover:opacity-80"
    />
  </Link>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location]);

  const go = (link) => (e) => {
    if (!link.hash) return;
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + link.hash);
    } else {
      scrollToTarget(link.hash);
    }
  };

  const isAnchorActive = (hash) => location.pathname === "/" && location.hash === hash;

  return (
    <header
      data-testid="rnb-navbar"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled || open ? "bg-black/90 backdrop-blur-xl border-b border-zinc-800/80" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-4 xl:gap-7" aria-label="Principal">
          {NAV_LINKS.map((link) =>
            link.to ? (
              <Link
                key={link.label}
                to={link.to}
                data-testid={`rnb-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-[11px] font-medium tracking-[0.2em] transition-colors hover:text-white ${
                  location.pathname === link.to ? "text-white" : "text-zinc-400"
                }`}
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.label}
                href={"/" + link.hash}
                onClick={go(link)}
                data-testid={`rnb-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-[11px] font-medium tracking-[0.2em] transition-colors hover:text-white ${
                  isAnchorActive(link.hash) ? "text-white" : "text-zinc-400"
                }`}
              >
                {link.label}
              </a>
            )
          )}
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to="/coaching-vip"
            data-testid="rnb-nav-cta-empieza-ahora"
            className="hidden sm:inline-block bg-white text-black font-display text-base tracking-[0.15em] px-6 py-2.5 hover:bg-zinc-200 transition-colors"
          >
            EMPIEZA AHORA
          </Link>
          <button
            data-testid="rnb-nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-white p-2"
            aria-label={open ? "Cerrar menú" : "Abrir menú"}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-b border-zinc-800"
            aria-label="Menú móvil"
          >
            <div className="px-6 py-6 flex flex-col gap-2">
              {NAV_LINKS.map((link) =>
                link.to ? (
                  <Link key={link.label} to={link.to} className="font-display text-2xl tracking-wider text-zinc-300 hover:text-white transition-colors py-2.5">
                    {link.label}
                  </Link>
                ) : (
                  <a key={link.label} href={"/" + link.hash} onClick={go(link)} className="font-display text-2xl tracking-wider text-zinc-300 hover:text-white transition-colors py-2.5">
                    {link.label}
                  </a>
                )
              )}
              <Link
                to="/coaching-vip"
                data-testid="rnb-nav-cta-empieza-ahora-mobile"
                className="mt-2 bg-white text-black text-center font-display text-xl tracking-[0.15em] px-6 py-3"
              >
                EMPIEZA AHORA
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
